/* @jsx jsx */
import { useEffect, useState } from 'react';

import Retracked from 'js/app/retracked';

import userPreferences from 'bundles/video-player/utils/userPreferences';

type AutoplayToggleConfig = {
  /**
   * A callback most commonly attached to a player.on('autoplaychange') event that subscribes to changes in the autoplay setting
   * @param callback A function be called when the autoplay setting changes
   */
  subscribeToAutoplayChanges: (callback: () => void) => void;
  /**
   * A callback most commonly attached to a player.off('autoplaychange') event that unsubscribes from changes in the autoplay setting
   * @param callback A function to be called when the subscription function is cleaned up and is no longer needed
   * @returns
   */
  unsubscribeFromAutoplayChanges: (callback: () => void) => void;
  /**
   * Callback (usually from the video player) to signal that the autoplay setting has changed
   */
  triggerAutoplayChangePlayerEvent: () => void;
};

const EVENTING_CONTEXT_DATA = {
  namespace: {
    app: 'open_course',
    page: 'video',
  },
};

export const useAutoplayToggle = ({
  subscribeToAutoplayChanges,
  unsubscribeFromAutoplayChanges,
  triggerAutoplayChangePlayerEvent,
}: AutoplayToggleConfig) => {
  // While we do get an event from `triggerAutoplayChangePlayerEvent()`, the event data doesn't include the autoplay state.
  // Logging an additional event as the quickest way to get the autoplay value into the events table, to support an experiment with autoplay.
  const logAutoplayChangeEvent = (newValue: boolean) => {
    const trackingData = { autoplay: newValue };
    Retracked.trackComponent(EVENTING_CONTEXT_DATA, trackingData, 'autoplay_toggle_standalone', 'click');
  };

  const [autoplay, setAutoplay] = useState<boolean>(userPreferences.get('autoplay'));

  useEffect(() => {
    // Update the component state when the autoplay setting was changed elsewhere
    // (Unknown how that would happen, but the other autoplay toggle control does this)
    const updateAutoplayUi = () => setAutoplay(userPreferences.get('autoplay'));
    subscribeToAutoplayChanges(updateAutoplayUi);
    return () => {
      unsubscribeFromAutoplayChanges(updateAutoplayUi);
    };
  }, [subscribeToAutoplayChanges, unsubscribeFromAutoplayChanges, triggerAutoplayChangePlayerEvent]);

  const handleToggleSwitchClick = () => {
    const newValue = !autoplay;
    setAutoplay(newValue);
    userPreferences.set('autoplay', newValue);
    triggerAutoplayChangePlayerEvent();

    logAutoplayChangeEvent(newValue);
  };

  return {
    autoplay,
    handleToggleSwitchClick,
  };
};
